import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth';
import { auth } from '@/data/firebase';

/**
 * 인증.
 *
 * ★ **1인 운영**이다. 가입 화면은 닫혀 있고 계정은 Firebase 콘솔에서 발급한다.
 *
 * ⚠️⚠️ **닫혀 있다는 것은 보안이 아니다.** LENS 는 웹 앱이라 API 키가
 * 브라우저에 노출되고, 앱을 거치지 않고 Auth REST API 로 계정을 만들 수 있다.
 * ★ **실제 차단은 `firestore.rules`** — `users/{uid}.approved == true` 가 아니면
 * `events` 를 한 건도 못 읽는다.
 *
 * ⚠️ 최초 1회: 콘솔 → Firestore → `users` → 본인 문서(ID = uid)에
 * `approved: true`, `role: "master"` 를 넣어야 한다. **없으면 본인도 못 읽는다.**
 */

export interface AuthState {
  user: User | null;
  /** 첫 상태를 아직 모르는 동안. ⚠️ 이때 로그인 화면으로 보내면 깜빡인다. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOutNow: () => Promise<void>;
  /**
   * 비밀번호 재설정 메일을 보낸다.
   *
   * ★★★ **왜 필요한가** — 2026-10-09 에 운영자가 비밀번호를 잊었는데
   * **푸는 길이 서비스 계정 키뿐**이었다. ⚠️ 운영자가 한 명이라 **아무도
   * 풀어줄 사람이 없다.**
   *
   * ⚠️ 재설정 화면은 **Firebase 가 호스팅하는 것**을 쓴다 — LENS 가 만들지
   * 않는다. 끝나면 `continueUrl` 로 돌아온다.
   */
  sendReset: (email: string) => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      signIn: async (email, password) => {
        await signInWithEmailAndPassword(auth, email, password);
      },
      signOutNow: async () => {
        await signOut(auth);
      },
      sendReset: async (email) => {
        // ★ 끝나면 로그인 화면으로 돌아온다. ⚠️ 이 주소는 **승인된 도메인**
        //   에 있어야 한다(lens.ingriti.com · lens-k2s9j1.web.app 둘 다 등록됨).
        await sendPasswordResetEmail(auth, email, {
          url: `${window.location.origin}/login`,
        });
      },
    }),
    [user, loading],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (v === null) throw new Error('AuthProvider 안에서만 쓸 수 있습니다.');
  return v;
}

/**
 * 로그인 실패를 **사람이 읽을 수 있게** 옮긴다.
 *
 * ★★ Flutter 쪽에서는 이것을 **할 수 없었다** — FF 의 `_signInOrCreateAccount`
 * 가 이미 영문 스낵바를 띄우고 `if (user == null) return;` 으로 빠져나가
 * 커스텀 문구를 끼워 넣을 자리가 없었다(CLAUDE.md). ★ 이식으로 풀린 제약이다.
 */
export function authErrorText(e: unknown): string {
  const code = typeof e === 'object' && e !== null && 'code' in e ? String(e.code) : '';
  switch (code) {
    case 'auth/invalid-email':
      return '이메일 형식이 아닙니다.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return '이메일 또는 비밀번호가 맞지 않습니다.';
    case 'auth/too-many-requests':
      return '시도가 많아 잠시 막혔습니다. 조금 뒤에 다시 해 보세요.';
    case 'auth/network-request-failed':
      return '네트워크에 닿지 못했습니다.';
    case 'auth/unauthorized-domain':
      // ⚠️ 이 화면이 없으면 「눌러도 아무 일이 없다」로 보인다.
      return '이 주소가 Firebase 의 「승인된 도메인」에 없습니다 — 콘솔에서 등록하세요.';
    default:
      return code === '' ? '로그인하지 못했습니다.' : `로그인하지 못했습니다 (${code}).`;
  }
}
